import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Globe, User, LogOut, Settings, Sparkles } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardNav() {
  const { user, token, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [xp, setXp] = useState(null);

  useEffect(() => {
    if (user?.role === 'student' && token) {
      axios.get(`${API}/gamification/profile/${user.id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setXp(res.data.xp))
        .catch(() => {});
    }
  }, [user, token]);

  const handleLogout = () => { logout(); navigate('/'); };
  const initials = user ? `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() : '';
  const profileImg = user?.profile_image ? `${API}/files/${user.profile_image}` : '';

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm" data-testid="dashboard-nav">
      {/* Top banner with flag-inspired accent */}
      <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #2563EB 33%, #FFFFFF 33%, #FFFFFF 50%, #DC2626 50%, #DC2626 66%, #012169 66%, #012169 100%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Slogans */}
          <Link to="/dashboard" className="flex items-center gap-4" data-testid="nav-logo">
            <h1 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Montserrat' }}>
              <span className="text-blue-600">Active</span><span className="text-red-500">English</span>
            </h1>
            <div className="hidden md:block border-l border-slate-200 pl-4">
              <p className="text-[10px] text-slate-400 italic leading-tight">{t('slogan1')}</p>
              <p className="text-[10px] text-slate-400 italic leading-tight">{t('slogan2')}</p>
            </div>
          </Link>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* XP / Insignias badge for students */}
            {user?.role === 'student' && xp !== null && (
              <button
                data-testid="xp-badge-nav"
                onClick={() => navigate('/progreso')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors text-sm font-bold text-amber-600 border border-amber-200"
              >
                <Sparkles className="w-4 h-4" />
                <span>{xp} pts</span>
              </button>
            )}

            {/* Notifications */}
            <NotificationBell />

            {/* Language Toggle */}
            <button
              data-testid="language-toggle-btn"
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors text-sm font-medium text-slate-600"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase text-xs font-bold">{language}</span>
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 focus:outline-none" data-testid="user-menu-trigger">
                  <Avatar className="h-9 w-9 border-2 border-blue-100">
                    <AvatarImage src={profileImg} alt={user?.first_name} />
                    <AvatarFallback className="bg-blue-500 text-white text-xs font-bold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-semibold text-slate-700 leading-tight">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-slate-400 capitalize">{t(user?.role || '')}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="nav-profile-btn">
                  <User className="w-4 h-4 mr-2" /> {t('profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="nav-settings-btn">
                  <Settings className="w-4 h-4 mr-2" /> {t('settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500 focus:text-red-500" data-testid="nav-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
